import {FILTERS} from '../constants/index';

/**
 * @typedef {object} Filters_Config
 * 
 * - {@link Compose_Filters_Config Used by `Compose_Filters_Config`}
 * 
 * @prop {string} category The name of the category being filtered
 * @prop {string} keyPath The path in the Vehicles_Object to filter by
 * @prop {string} groupingPath the path to group the filters by
 */

/**
 * @typedef {object} Compose_Filters_Config
 * 
 * - {@link composeFiltersUiShape for example see `composeFiltersUiShape`}
 * - {@link Filters_Config see also `Filters_Config`}
 * 
 * @prop {array} items Array of items to create a filters state shape from
 * @prop {string} storeKey Key path for state to save data
 * @prop {Filters_Config[]} filters Sets of Filters configuration objects
 * @prop {Function} labelFn A label function to group names
 * @prop {array} [segments] additional filters against results
 */

/**
 * @name composeFiltersUiShape
 * @desc Create a filters state shape based on an existing array of objects
 * @param {Compose_Filters_Config} config 
 * @returns {{type: string, items: Array, storeKey: string, filters: Array, labelFn:Function, segments: Array}}
 * @memberof actions
 * 
 * @example <caption>Compose Filters UI Shape Configuration (Compose_Filters_Config)</caption>
 * // For config object Compose_Filters_Config {items, storeKey, filters, labelFn, segments})
 * // where the following properties are provided:
 * 
 * // items the array of objects to filter, typically vehicles
 * const items = [{...}, {...}, {...}]; 
 * 
 * // where to put new shape; state.gmi.ui.filters[storeKey]
 * const storeKey = 'car_classes'; 
 * 
 * // using the supplied `Filters_Config`, filter by `keyPath` in each `item` object using AND logic
 * // - each config produces one filter in state
 * // - each `Filters_Config` object will produce a filter object in the gmi filters state path
 * // e.g. gmi.ui.filters.car_classes.filters
 * const filters = [
 *   {category:'Vehicle Class', keyPath:'filters.CLASS.filter_description'}, // a Filters_Config
 *   {category:'Transmission', keyPath:'filters.TRANSMISSION.filter_description'}, // a Filters_Config
 *   {category:'Passengers', keyPath:'filters.PASSENGERS.filter_description'} // a Filters_Config
 * ];
 * 
 * // `labelFn` is optional. get a label based on value provided. if omitted, label === value
 * // The object param contains either the `keyPath` or `groupingPath` depending on if this is a group being labeled
 * // - this is used inside the code that generates the `filters` array object passed to state
 * const labelFn = (value, {keyPath||groupingPath}) => {
 *   // logic to determine a label from value
 *   return label;
 * };
 * 
 * // segments are optional. they allow for filtering of items using OR logic ahead of filters which use AND logic
 * // unlike filters, we include values here to determine if segmentsToggleIsVisible === true when initialized
 * const segments = [
 *  {keyPath:'status', values:'VEHICLE_AVAILABLE_AT_PROMOTIONAL_RATE'},
 *  {keyPath:'coupons', values:value => Array.isArray(value)},
 *  {keyPath:'redemption_available', values:true}
 * ];
 * 
 * dispatch(actions.composeFiltersShape({items, storeKey, filters, labelFn, segments}));
 * // See next example for output.
 * 
 * @example <caption>GMI State Shape Output</caption>
 * // The above call creates the following Redux State at `gmi.ui.filters.car_classes`
 * GMI_FILTERS_SHAPE { 
 *   items: array,
 *   nextFilters: object,
 *   filters: array, // each based on `Filters_Config`
 *   filtersIsVisible: boolean,
 *   numFiltersSelected: number,
 *   segments: array,
 *   segmentsIsActive: boolean,
 *   segmentsToggleIsVisible: boolean
 * }
 * // in detail:
 * {
 *   items: {
 *     all: [...],
 *     segmented: [...],
 *     filtered: [...]
 *    },
 *   // This gets populated via setNextFilteredUiByKey; preview results for setting filters without setting them
 *   nextFilters: {},
 *  
 *   // a segments object is only added if there is >=1 item with a matching value at the defined keyPath
 *   segments: [
 *   {keyPath:'status', values:'VEHICLE_AVAILABLE_AT_PROMOTIONAL_RATE'},
 *   {keyPath:'coupons', values:value => Array.isArray(value)},
 *   {keyPath:'redemption_available', values:true}
 *  ],
 *  
 *   // a filters object is only added if there are >1 matching item at the defined `keyPath`
 *   // each entry matches the passed in `Filters_Config` from the input `filters` array, i.e. Filters_Config[]
 *   filters: [
 *    // derived from {category:'Vehicle Class', keyPath:'filters.CLASS.filter_description'} above
 *    {
 *      category: 'Vehicle Class',
 *      keyPath: 'filters.CLASS.filter_code', // matches input `keyPath`
 *      available: [
 *        // isVisible is determined by the keyPath existing in a segmented array item
 *        // it will be set to false if the checkbox should be hidden while segmentsIsActive === true
 *        {label:'Cars', value:'100' isVisible: true, checked: false},
 *        {label:'SUVs', value:'300', isVisible: true, checked: false}
 *        // etc...
 *      ],
 *      isVisible: true, // >1 available isVisible makes this true
 *      values: null, // set via actions.setFiltersByKey
 *    },
 *    {
 *      category: 'Transmission',
 *      keyPath: 'filters.TRANSMISSION.filter_code',
 *      available: [
 *        {label:'Automatic', value:'25' isVisible: true, checked: false},
 *        {label:'Manual', value:'26', isVisible: true, checked: false}
 *      ],
 *      isVisible: true,
 *      values: null
 *    },
 *    {
 *      category: 'Passengers',
 *      keyPath: 'filters.PASSENGERS.filter_code',
 *      // see setFiltersUiByKey example for how to have available items behave as radio buttons
 *      available: [
 *        {label:'4', value:'4', isVisible: true, checked: false},
 *        {label:'5', value:'5', isVisible: true, checked: false},
 *        {label:'6', value:'6', isVisible: true, checked: false},
 *        {label:'7', value:'7', isVisible: true, checked: false},
 *        {label:'8', value:'8', isVisible: true, checked: false},
 *        {label:'9', value:'9', isVisible: true, checked: false}
 *      ],
 *      isVisible: true,
 *      values: null
 *    }
 *   ],
 *   numFiltersSelected: 0, // updated via actions.setFiltersByKey
 *   filtersIsVisible: true, // >=1 filter object isVisible
 *   // both segmentsIsActive and segmentsToggleIsVisible are true if segments.length > 0
 *   segmentsIsActive: true,
 *   segmentsToggleIsVisible: true
 * }
 * 
 * @example <caption>Group By A Different `keyPath`</caption>
 * // filter by keyPath but group by another object property
 * const filters = [
 *   {category:'Vehicle Class', keyPath:'subcategory', groupingPath:'category'}
 * ];
 * 
 * // Shape Results filters and available array would then look something like...
 * filters: [
 *  {
 *    category: 'Vehicle Class',
 *    keyPath: 'subcategory',
 *    available: [
 *      {
 *        groupingValue: '100',
 *        groupingLabel: 'Cars',
 *        groupingAvailable: [{label:'Economy Cars', value:'101', isVisible:true, checked:false}]
 *      },
 *      {
 *        groupingValue: '300',
 *        groupingLabel: 'SUVs',
 *        groupingAvailable: [{label:'Midsize SUVs', value:'301', isVisible:true, checked:false}]
 *      }
 *    ],
 *    isGrouped: true,
 *    isVisible: true,
 *    values: null
 *  }
 * ]
 */
export function composeFiltersUiShape(config) {
  const {items = [], storeKey = '', filters = [], segments = []} = config;
  return {type: FILTERS.COMPOSE_FILTERS_UI_SHAPE, items, storeKey, filters, labelFn:config.labelFn, segments};
}

/**
 * @name setFiltersUiByKey
 * @desc Set the filter values for each object key path
 * @param {String} storeKey location to update filter values for; gmi.ui.filters[storeKey]
 * @param {Array} filters array of objects of filter values for each prop
 * @returns {{type: string, filters: *}}
 * @memberof actions
 * @example
 * const storeKey = 'car_classes';
 * const filters = [
 *  {keyPath:'filters.CLASS.filter_code', values:['100']},
 *  {keyPath:'filters.TRANSMISSION.filter_code', values:['25']},
 *  {keyPath:'filters.PASSENGERS.filter_code', values:value => parseInt(value, 10) >= 6, radioValue:'6'}
 *  // if `radioValue` is provided, the array is filtered on `values` fn, but only item with value === radioValue is checked:true
 *  // you can use `radioValue` with any other `values` type as well; it is not exclusive to select functions...
 *  // i.e. {keyPath: 'filters.CLASS.filter_code', values: ['100', '200', '300'], radioValue: '300'}
 * ];
 * dispatch(actions.setFiltersByKey(storeKey, filters));
 *
 * // will update the following at gmi.ui.filters.car_classes...
 * {
 *  items: {
 *    all: [...],
 *    segmented: [...],
 *    // now only objects that match filter values provided above
 *    // will filter either 'all' or 'segmented' based on value of segmentsIsActive
 *    filtered: [...]
 *  },
 *  // this gets populated via setNextFilteredUiByKey; it gets reset to empty object when setFiltersUiByKey is dispatched
 *  nextFilters: {},
 *  // segments data remains unchanged
 *  segments: [...],
 *  // filters objects will be updated with new `filter[i].values` and `filter[i].available[j].checked` values
 *  // the `filter[i].available[j].isVisible` and `filter[i].isVisible` values remain unchanged by this action
 *  // but they DO get updated by the toggleSegmentsUiByKey action
 *  filters: [
 *    {
 *      category: 'Vehicle Class',
 *      keyPath: 'filters.CLASS.filter_code',
 *      available: [
 *        {label:'Cars', value:'100', isVisible:true, checked:true},
 *        {label:'SUVs', value:'300', isVisible:true, checked:false}
 *        // etc...
 *      ],
 *      isVisible: true,
 *      values: ['100'],
 *    },
 *    {
 *      category: 'Transmission',
 *      keyPath: 'filters.TRANSMISSION.filter_code',
 *      available: [
 *        {label:'Automatic', value:'25', isVisible:true, checked:true},
 *        {label:'Manual', value:'26', isVisible:true, checked:false}
 *      ],
 *      isVisible: true,
 *      values: ['25'],
 *    },
 *    {
 *      category: 'Passengers',
 *      keyPath: 'filters.PASSENGERS.filter_code',
 *      available: [
 *        {label:'4', value:'4', isVisible:true, checked:false},
 *        {label:'5', value:'5', isVisible:true, checked:false},
 *        {label:'6', value:'6', isVisible:true, checked:true},
 *        {label:'7', value:'7', isVisible:true, checked:false},
 *        {label:'8', value:'8', isVisible:true, checked:false},
 *        {label:'9', value:'9', isVisible:true, checked:false},
 *      ],
 *      isVisible: true,
 *      values: value => parseInt(value, 10) >= 6
 *      // as noted above...
 *      // if `radioValue` is provided, the array is filtered on `values` fn, but only item with value === radioValue is checked:true
 *      // you can use `radioValue` with any other `values` type as well; it is not exclusive to select functions...
 *      // i.e. {keyPath: 'filters.CLASS.filter_code', values: ['100', '200', '300'], radioValue: '300'}
 *      radioValue: '6'
 *    }
 *  ],
 *  // a single value (i.e. string, number, selector fn) counts as "1", an array of values counts as array.length
 *  // this number can be changed by the toggleSegmentsUiByKey if a filter isVisible value changes
 *  numFiltersSelected: 3,
 *  // >=1 filter object isVisible
 *  filtersIsVisible: true,
 *  // segmentsIsActive remains unchanged; can only change via toggleSegmentsUiByKey
 *  segmentsIsActive: true,
 *  // segmentsToggleIsVisible might update...
 *  // if segmentsIsActive === false, we check to make sure segmenting with current filters would not generate 0 results
 *  segmentsToggleIsVisible: true
 * }
 *
 * // you can clear/uncheck all filters for a fieldset by either setting their value to null or omitting them
 * const filters = [
 *  {keyPath:CLASS, values:null},
 *  // omitted TRANSMISSION obj
 *  {keyPath:PASSENGERS, values:null}
 * ];
 * dispatch(actions.setFiltersByKey(storeKey, filters));
 */
export function setFiltersUiByKey(storeKey, filters) {
  if (!Array.isArray(filters)) filters = [];
  return {type: FILTERS.SET_FILTERS_UI_VALUES, storeKey, filters};
}

/**
 * @name toggleSegmentsUiByKey
 * @desc Toggle on/off segments by a redux store key
 * @param {String} storeKey
 * @returns {{type: string, storeKey: string}}
 * @memberof actions
 * @example
 * // toggle previously set segments on/off
 * // for the purposes of this example, we'll assume it was inactive
 * // and the below statement is setting segmentsIsActive to true
 * dispatch(actions.toggleSegmentsUiByKey(storeKey, filters));
 *
 * // will update the following at gmi.ui.filters.car_classes...
 * // the value of gmi.ui.filters.car_classes.segmentsIsActive gets toggled
 * // and other functionality outlined below happens based on that
 * {
 *  items: {
 *    all: [...],
 *    segmented: [...],
 *    // will filter either 'all' or 'segmented' based on value of segmentsIsActive
 *    filtered: [...]
 *  },
 *  // nextFilters remains unchanged; its assumed its empty when toggleSegmentsUiByKey is dispatched
 *  nextFilters: {},
 *  // segments data remains unchanged
 *  segments: [...],
 *  // filters `filter[i].values` and `filter[i].available[j].checked` values remain unchanged
 *  // the `filter[i].available[j].isVisible` and `filter[i].isVisible` values get updated...
 *  // if segmentsIsActive === true, each available item is checked against the `segmented` array to make sure at least 1 exists
 *  // if it exists, isVisible === true, else isVisible === false
 *  filters: [
 *    {
 *      category: 'Vehicle Class',
 *      keyPath: 'filters.CLASS.filter_code',
 *      available: [
 *        // assuming our segmented removed SUVs...
 *        {label:'Cars', value:'100', isVisible:true, checked:true},
 *        {label:'SUVs', value:'300', isVisible:false, checked:false}
 *        // etc...
 *      ],
 *      // >1 available isVisible makes this true
 *      // so since only 1 isVisible, this becomes false
 *      isVisible: false,
 *      values: ['100'],
 *    },
 *    {
 *      category: 'Transmission',
 *      keyPath: 'filters.TRANSMISSION.filter_code',
 *      available: [
 *        {label:'Automatic', value:'25', isVisible:true, checked:true},
 *        {label:'Manual', value:'26', isVisible:true, checked:false}
 *      ],
 *      isVisible: true,
 *      values: ['25'],
 *    },
 *    {
 *      category: 'Passengers',
 *      keyPath: 'filters.PASSENGERS.filter_code',
 *      available: [
 *        {label:'4', value:'4', isVisible:true, checked:false},
 *        {label:'5', value:'5', isVisible:true, checked:false},
 *        {label:'6', value:'6', isVisible:true, checked:true},
 *        // by segmenting on cars, larger vehicle filter options are now hidden
 *        {label:'7', value:'7', isVisible:false, checked:false},
 *        {label:'8', value:'8', isVisible:false, checked:false},
 *        {label:'9', value:'9', isVisible:false, checked:false},
 *      ],
 *      isVisible: true,
 *      values: value => parseInt(value, 10) >= 6
 *      radioValue: '6'
 *    }
 *  ],
 *  // even though '100' is 'checked', since that fieldset isVisible === false
 *  // we do not include it in numFiltersSelected, so this number becomes '2' instead of '3'
 *  numFiltersSelected: 2,
 *  // >=1 filter object isVisible
 *  filtersIsVisible: true,
 *  // this now gets set to true if it was false before this action was dispatched
 *  segmentsIsActive: true,
 *  // this is clearly true since we just hit the toggle button
 *  segmentsToggleIsVisible: true
 * }
 */
export function toggleSegmentsUiByKey(storeKey) {
  return {type: FILTERS.TOGGLE_SEGMENTS_UI, storeKey};
}

/**
 * @name setNextFilteredUiByKey
 * @desc Preview how many filtered items there would be given filters provided; it does NOT update the items arrays!
 * @param {String} storeKey location to update filter values for; gmi.ui.filters[storeKey]
 * @param {Array} filters array of objects of filter values for each prop
 * @returns {{type: string, filters: *}}
 * @memberof actions
 * @example
 * const storeKey = 'car_classes';
 * const filters = [
 *  {keyPath:'filters.CLASS.filter_code', values:['100']},
 *  {keyPath:'filters.TRANSMISSION.filter_code', values:['25']},
 *  {keyPath:'filters.PASSENGERS.filter_code', values:value => parseInt(value, 10) >= 6, radioValue:'6'}
 * ];
 * dispatch(actions.setNextFilteredUiByKey(storeKey, filters));
 *
 * // will update the following at gmi.ui.filters.car_classes...
 * {
 *  items: {
 *    all: [...],
 *    segmented: [...],
 *    // this filtered array remains unchanged; its only updated via setFiltersUiByKey
 *    filtered: [...]
 *  },
 *  // nextFilters gets updated to reflect what would happen if setFiltersUiByKey was dispatched with the same filters
 *  nextFilters: {
 *    filters, // *(Array)*: the value of action.filters,
 *    filteredLength, *(Number)*: the length car_classes.items.filtered would be if these filters were applied,
 *    numFiltersSelected *(Number)*: the value car_classes.numFiltersSelected would be if these filters were applied
 *  },
 *  // everything below also remains unchanged
 *  segments: [...],
 *  filters: [...],
 *  numFiltersSelected, // *(Number)*
 *  filtersIsVisible, // *(Boolean)*
 *  segmentsIsActive, // *(Boolean)*
 *  segmentsToggleIsVisible // *(Boolean)*
 * }
 *
 * // you can cancel/reset the nextFilters object by either setting the filters value to null or omitting them
 * const filters = [
 *  {keyPath:CLASS, values:null},
 *  {keyPath:TRANSMISSION, values:null},
 *  {keyPath:PASSENGERS, values:null}
 * ];
 * // ...or...
 * // const filters = [];
 * dispatch(actions.setNextFilteredUiByKey(storeKey, filters));
 */
export function setNextFiltersUiByKey(storeKey, filters) {
  if (!Array.isArray(filters)) filters = [];
  return {type: FILTERS.SET_NEXT_FILTERS_UI, storeKey, filters};
}

/**
 * @name saveFilteredArrays
 * @desc Adds filtered arrays to redux store at `state.gmi.data.filters[storeKey][filters[i].oKeyPath][filters[i].storeKey]`
 * @param {Array} items
 * @param {String} storeKey
 * @param {Array} filters
 * @returns {{type: string, items: Array, storeKey: string, filters: Array}}
 * @memberof actions
 * @example
 * const items = [
 *  {code:'AFHR'},
 *  {code:'ADDR'},
 *  {code:'TBD'},
 *  {nested: {path:1}}
 * ];
 * const storeKey = 'location_policies';
 * const filters = [
 *  {oKeyPath:'code', storeKey:'AFHR', value:'AFHR'},
 *  {oKeyPath:'code', storeKey:'ADDR', value:['ADDR']},
 *  {oKeyPath:'code', storeKey:'TBD', value:(value) => value === 'TBD'},
 *  {oKeyPath:'nested.path', storeKey:'DEBT', value:1}
 * ];
 * dispatch(actions.saveFilteredArrays(items, storeKey, filters));
 *
 * // will save the following at `gmi.data.filters.location_policies`...
 * console.log(state.gmi.data.filters.location_policies['code']['AFHR']) // [{code:'AFHR'}]
 * console.log(state.gmi.data.filters.location_policies['code']['ADDR']) // [{code:'ADDR'}]
 * console.log(state.gmi.data.filters.location_policies['code']['TBD']) // [{code:'TBD'}]
 * console.log(state.gmi.data.filters.location_policies['nested.path']['DEBT']) // [{nested: {path:1}}]
 */
export function saveFilteredArrays(items = [], storeKey = '', filters = [])  {
  return {type: FILTERS.SAVE_FILTERED_ARRAYS, items, storeKey, filters};
}

/* @desc Sorts the filtered array by a sortBy value with an ascending or descending order
 * @param {String} storeKey
 * @param {Array} sortByKeyPath
 * @param {Boolean} isDesc
 * @param {Boolean} isNumber
 * @param {String} inputValue
 * @returns {{type: string, storeKey: string, sortByKeyPath: Array, isDesc: Boolean, isNumber: Boolean, inputValue: String}}
 * @memberof actions
 * @example
 * const storeKey = 'car_classes'; // where to put new sorted array => state.gmi.ui.filters[storeKey].items.filtered_sorted
 * const sortByKeyPath = ['charges','PAYLATER', 'total_price_payment','amount'];
 * const isDesc: false;
 * const isNumber: true;
 * const inputValue: 'sort_low_high';
 * 
 * dispatch(actions.setSortFilteredItemsByValue(storeKey, sortByKeyPath, isDesc, isNumber, inputValue));
 */
export function setSortFilteredItemsByValue(storeKey, sortByKeyPath, isDesc, isNumber, inputValue) {
  return {type: FILTERS.SORT_FILTERED_UI_VALUES, storeKey, sortByKeyPath, isDesc, isNumber, inputValue};
}
